import { Component, DestroyRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SlipState } from '@bpos/v1/my-bets';
import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { EarlyPayoutConfig, MyBetsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { timeSpanToMS } from '@frontend/sports/common/core/utils/date';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { Nullable, SimpleCallback } from '@frontend/sports/common/core/utils/extended-types';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { DsButtonKind, DsButtonSize } from '@frontend/ui/button';
import { DarkModeService } from '@frontend/vanilla/shared/dark-mode';
import { filter } from 'rxjs';

import { EpcotConfigService, EpcotModule } from '../../../common/epcot-config.service';
import { GeolocationCheckService } from '../../../geo-location/geo-location-check.service';
import { EarlyPayoutSubmitMyBetsService } from '../../../my-bets-base/early-payout/early-payout-injection-tokens';
import {
    EarlyPayoutTACSource,
    EarlyPayoutTacService,
    IEarlyPayoutTACTrackingData,
} from '../../../my-bets-base/early-payout/early-payout-tac.service';
import { EarlyPayoutUtils } from '../../../my-bets-base/early-payout/early-payout-utils';
import { MyBetsEarlyPayoutButtonTrackingService } from '../../../my-bets-base/early-payout/my-bets-earlypayout-button-tracking.service';
import { EarlyPayoutHelpers } from '../../../my-bets-base/early-payout/my-bets-earlypayout-helpers';
import { EarlyPayoutFailedResponse, EarlyPayoutSuccessResponse } from '../../../my-bets-base/models/early-payout';
import { EarlyPayoutStates } from '../../../my-bets-base/models/early-payout-types';
import { MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { EarlyPayoutDispatcherEvent } from '../../models/early-payout.model';
import { MyBetsStrategy } from '../../services/my-bets-display.strategy';
import { EarlypayoutButtonState, MyBetsEarlypayoutButtonStateService } from '../../services/my-bets-earlypayout-button-state.service';
import { MyBetsUtilService } from '../../services/my-bets-util.service';

@Component({
    selector: 'ms-my-bets-earlypayout-button',
    templateUrl: 'my-bets-earlypayout-button.html',
    styleUrl: 'my-bets-earlypayout-button.scss',
    standalone: false,
})
export class MyBetsEarlyPayoutButtonComponent implements OnInit, OnDestroy {
    @Input() betslip: MyBetsBetSlip;
    @Input() myBetsListContext: MyBetsListContext;
    @Input() betTypeInfo: string;
    @Input() payoutPushError?: { Title: string; Message: string };
    @Output() stateChanged: EventEmitter<void> = new EventEmitter<void>();
    @HostBinding('class.cashout-dark') darkModeClass: string;
    isNotSettledTab = computed(() => this.myBetsListContext.listType !== BetslipListType.Settled);
    private buttonResetTimerClean: Nullable<SimpleCallback> = null;
    private readonly epTimeoutDelay: number;
    cashoutButtonStates = EarlypayoutButtonState;
    module = EpcotModule.MyBetsBetSlip;
    isEpcotEnabled: boolean;
    betCardRedesigned = this.myBetsUtilService.isBetCardRedesigned();
    readonly buttonThemeKind: ThemeOptions<DsButtonKind> = {
        'mgm-4': 'primary',
        'sports-interaction': 'primary',
        'betboo-br': 'primary',
        'sportingbet-br': 'primary',
    };

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private earlyPayoutUtils: EarlyPayoutUtils,
        private dispatcher: DispatcherService,
        private myBetsConfig: MyBetsConfig,
        private balanceService: AccountBalanceService,
        private earlyPayoutTacService: EarlyPayoutTacService,
        private eyBetsEarlypayoutButtonStateService: MyBetsEarlypayoutButtonStateService,
        private timerService: TimerService,
        private earlyPayoutSubmitService: EarlyPayoutSubmitMyBetsService,
        private myBetsStrategy: MyBetsStrategy,
        private geolocationCheckService: GeolocationCheckService,
        private trackingService: MyBetsEarlyPayoutButtonTrackingService,
        private earlypayoutConfig: EarlyPayoutConfig,
        private epcotConfigService: EpcotConfigService,
        private darkModeService: DarkModeService,
        private earlyPayoutHelpers: EarlyPayoutHelpers,
        private myBetsUtilService: MyBetsUtilService,
    ) {
        this.epTimeoutDelay = this.myBetsConfig.earlyPayoutButtonResetTime ? timeSpanToMS(this.myBetsConfig.earlyPayoutButtonResetTime) : 5000;
        this.isEpcotEnabled = this.epcotConfigService.isEnabled(this.module);
    }

    ngOnInit(): void {
        this.darkModeClass = this.darkModeService.isEnabled ? 'cashout-dark' : '';
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
        this.dispatcher
            .on(EarlyPayoutDispatcherEvent.CashOut.Updated)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.stateChanged.emit());

        this.dispatcher
            .on(EarlyPayoutDispatcherEvent.CashOut.PushToLower)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.resetPreparingState.bind(this));

        this.dispatcher
            .on<EarlyPayoutSuccessResponse>(EarlyPayoutDispatcherEvent.CashOut.EarlyPayoutSuccess)
            .pipe(
                filter((response) => response.betslipId === this.betslip.betslipRealId),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((response) => this.handleEarlyPayoutSuccess(response));
        this.dispatcher
            .on<EarlyPayoutFailedResponse>(EarlyPayoutDispatcherEvent.CashOut.EarlyPayoutFailed)
            .pipe(
                filter((response) => response.betslipId === this.betslip.betslipRealId),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((response) => this.handleEarlyPayoutPlaceError(response));
    }

    get isAvailable(): boolean | undefined {
        if (this.betCardRedesigned) {
            return this.earlyPayoutUtils.isEarlyPayoutAvailable(this.betslip) && !this.earlyPayoutHelpers.earlyPayoutIsNotOffered(this.betslip);
        }
        return this.earlyPayoutUtils.isEarlyPayoutAvailable(this.betslip);
    }

    get buttonState(): EarlypayoutButtonState {
        return this.eyBetsEarlypayoutButtonStateService.buttonState(this.betslip);
    }

    get payoutValue(): number {
        return this.betslip.earlyPayout!.valueWithoutStake || this.betslip.earlyPayout!.value;
    }

    getButtonClasses(): { [key: string]: boolean } {
        return {
            'not-available': this.buttonState === this.cashoutButtonStates.Disabled,
            'error-message': !!this.payoutPushError,
            'allowed': this.buttonState === this.cashoutButtonStates.Allowed,
            'confirmation': this.buttonState === this.cashoutButtonStates.Confirm,
            'processing': this.buttonState === this.cashoutButtonStates.Preparing,
            'paidOut': this.buttonState === this.cashoutButtonStates.PaidOut,
            'completed': this.buttonState === this.cashoutButtonStates.Completed,
        };
    }

    async submitEarlyPayout($event?: Event): Promise<void> {
        if (!this.betslip.earlyPayout) {
            return;
        }

        if (this.buttonState === EarlypayoutButtonState.Disabled) {
            if ($event) {
                $event.stopPropagation();
                $event.preventDefault();
            }

            return;
        }

        const hasUserAcceptedTAC = this.earlyPayoutTacService.hasUserAcceptedTAC;
        if (this.earlypayoutConfig.isEarlyPayoutTACDialogueEnabled && !hasUserAcceptedTAC) {
            const trackingData: IEarlyPayoutTACTrackingData = {
                source: this.myBetsListContext.isEventView ? EarlyPayoutTACSource.EventView : EarlyPayoutTACSource.MyBets,
                forLiveBet: this.betslip.containsLiveBets,
            };
            const result = await this.earlyPayoutTacService.acceptEarlyPayoutTaC(trackingData);
            if (!result) {
                return;
            }
        }

        this.betslip.earlyPayout.error = undefined; // Do we need this?

        const context = this.myBetsListContext.isEventView ? '_eventView' : this.myBetsStrategy.inColumn ? '_Mini_MyBets' : '_Main_MyBets';
        const trackModel = {
            earlyPayoutSlipID: this.betslip.betslipRealId,
            earlyPayoutValue: this.payoutValue,
            containsLiveBets: this.betslip.containsLiveBets,
            context,
            betType: this.betTypeInfo,
            isFreeBet: this.betslip.isFreeBet,
        };

        switch (this.buttonState) {
            case EarlypayoutButtonState.Allowed:
                this.betslip.earlyPayout.state = EarlyPayoutStates.Confirm;
                // reset earlypayout state after 5sec
                this.buttonResetTimerClean = this.timerService.setTimeout(() => this.resetPreparingState(), this.epTimeoutDelay);

                this.trackingService.trackCashoutClick(trackModel);

                break;
            case EarlypayoutButtonState.Confirm:
                this.betslip.earlyPayout.state = EarlyPayoutStates.Preparing;
                // Always send value received from backend in userExpectedValue
                this.betslip.earlyPayout.userExpectedValue = this.betslip.earlyPayout.value;

                if (this.buttonResetTimerClean) {
                    this.buttonResetTimerClean();
                }

                this.trackingService.trackCashoutConfirm(trackModel);

                this.geolocationCheckService.checkGeolocation(
                    (isLocationFailed) => {
                        if (isLocationFailed && this.betslip.earlyPayout) {
                            this.betslip.earlyPayout.state = EarlyPayoutStates.Allowed;

                            return;
                        }
                        this.earlyPayoutSubmitService.submitPayout(this.betslip, context, this.betTypeInfo);
                    },
                    { waitForLocationAvailable: true },
                );

                break;
        }

        this.stateChanged.emit();

        return;
    }

    private handleEarlyPayoutSuccess(response: EarlyPayoutSuccessResponse): void {
        this.onBetPaidOut();

        this.betslip.possibleWinnings = {
            value: response.payoutWithoutStake || response.payout,
            currency: this.betslip.payoutAmount.currency,
        };
        this.betslip.earlyPayout!.value = 0;
        this.betslip.earlyPayout!.state = EarlyPayoutStates.PaidOut;
        this.betslip.state = SlipState.Won;
        this.betslip.isCachedOut = true;

        if (this.betslip.possibleWinnings) {
            this.betslip.payoutAmount = {
                value: this.betslip.possibleWinnings.value,
                currency: this.betslip.possibleWinnings.currency,
            };
        }
        this.stateChanged.emit();
    }

    private handleEarlyPayoutPlaceError(response: EarlyPayoutFailedResponse): void {
        this.earlyPayoutUtils.handleEarlyPayoutPlaceError(response, this.betslip.earlyPayout);
        this.stateChanged.emit();
    }

    private resetPreparingState(isPushUpdate?: boolean): void {
        if (this.betslip.earlyPayout && this.betslip.earlyPayout.state === EarlyPayoutStates.Confirm) {
            this.betslip.earlyPayout.state = EarlyPayoutStates.Allowed;

            this.trackingService.trackCashoutResetPreparing({
                earlyPayoutSlipID: this.betslip.betslipRealId,
                earlyPayoutValue: this.payoutValue,
                containsLiveBets: this.betslip.containsLiveBets,
                context: this.myBetsListContext.isEventView ? '_eventView' : this.myBetsStrategy.inColumn ? '_Mini_MyBets' : '_Main_MyBets',
                isPushUpdate: !!isPushUpdate,
                betType: this.betTypeInfo,
                isFreeBet: this.betslip.isFreeBet,
            });
            this.stateChanged.emit();
        }
    }

    private onBetPaidOut(): void {
        this.balanceService.refresh$().subscribe();
    }

    get buttonSize(): DsButtonSize {
        if (this.betCardRedesigned) {
            return 'medium';
        } else {
            return 'large';
        }
    }

    get buttonKind(): DsButtonKind {
        if (this.betCardRedesigned) {
            return 'primary';
        } else {
            return 'success';
        }
    }

    ngOnDestroy(): void {
        this.timerService.removeAll();
    }
}
